/**
 * @fileoverview `buildDiscoverPersonalization` — single source of truth for the
 * personalized Discover chrome (FRE-917).
 *
 * The personalized "Discover, {name}." framing layers across four chrome
 * surfaces — the coord strip taste segment, the hero title + taste meta line,
 * the results-bar sort label, and the app footer identity.
 * Each surface must gate identically so the page never shows a half-personalized
 * state (e.g. a name in the title but generic chrome everywhere else). This
 * module owns that gate and the honest field derivation so the gating logic
 * cannot drift between the chrome elements.
 *
 * **Honesty contract (epic guardrail — never fabricate).** The personalized
 * layer renders ONLY when the viewer is authenticated AND the response carries
 * the authenticated envelope AND `meta.personalized === true` (a real taste
 * signal, not a still-"soaking" cold-start account). When any of those is
 * absent, this returns `null` and every consumer falls back to today's generic
 * (A) chrome. Individual fields are emitted only when a real value backs them —
 * a missing display name, an omitted `tasteMatchPct`, or zero followed
 * artists/venues each drop their own sub-fragment rather than render a fabricated
 * placeholder.
 *
 * @since FRE-917 (Discover · personalized framing — "Discover, {name}" + taste meta)
 */
import type { User } from '@loci/api';
import type { DiscoverAuthenticatedEnvelope } from '@loci/types';

/**
 * Inputs the gate reads. `user` + `isAuthenticated` come from `useAuth()`;
 * `envelope`, `personalized`, and `total` come from the shared
 * `useDiscoverEventsQuery` subscription (no second fetch — the cold-load
 * single-fetch invariant is preserved).
 */
export interface DiscoverPersonalizationInput {
  /** Current user from `useAuth()`. `null` when signed out / still loading. */
  readonly user: User | null;
  /** `useAuth().isAuthenticated`. */
  readonly isAuthenticated: boolean;
  /** Authenticated-only envelope from the discover response. Absent for anon. */
  readonly envelope: DiscoverAuthenticatedEnvelope | undefined;
  /**
   * `meta.personalized` from the discover response. `true` = real taste signal;
   * `false`/`undefined` = signed in but soaking, or anonymous. The gate requires
   * an explicit `true`.
   */
  readonly personalized: boolean | undefined;
  /** `meta.total` — the unfiltered catalog total used in `N of M match`. */
  readonly total: number | undefined;
  /**
   * API-1671 corpus-wide `meta.tasteMatchedTotal`. `null` = no taste
   * signal. `undefined` = not yet read. A number (including `0`) is the
   * measured corpus-wide match count — never a page-local `tasteMatchPct`
   * tally (WEB-786).
   */
  readonly tasteMatchedTotal: number | null | undefined;
}

/**
 * Resolved personalized-chrome model. When this is non-null every consumer
 * renders the personalized layer; when it is `null` consumers render the
 * generic (A) chrome.
 */
export interface DiscoverPersonalization {
  /**
   * Real viewer first/display name for `Discover, {name}.`. `null` when no
   * honest name exists (the title then keeps the generic `Discover, {city}.`).
   */
  readonly viewerName: string | null;
  /**
   * Corpus-wide taste-matched total (`meta.tasteMatchedTotal`). Drives
   * `N of M match your taste`. `null` when the field is not a measured
   * number — the coord-strip taste segment then stays generic.
   */
  readonly tasteMatchCount: number | null;
  /**
   * Unfiltered catalog total (`M` in `N of M match your taste`). A measured
   * `0` is preserved so a resolved empty result set reads `0 of 0` rather
   * than silently dropping the taste segment (WEB-786). `null` only when
   * `meta.total` has not resolved.
   */
  readonly totalCount: number | null;
  /** Number of followed artists with events on the page; `0` when none. */
  readonly followedArtistCount: number;
  /** Number of followed venues with events on the page; `0` when none. */
  readonly followedVenueCount: number;
}

/**
 * Real display name for the personalized title: prefer `firstName`, then the
 * first whitespace-delimited token of `name`. Returns `null` when neither yields
 * a non-empty value — the title then keeps the generic city grammar rather than
 * inventing a name. The email local-part is intentionally NOT used here (unlike
 * the footer's neutral `Member` fallback): a raw email handle reads wrong in the
 * prominent `Discover, {name}.` headline, so an honest absence is preferred.
 *
 * Email-shaped `name` fallbacks are rejected too: if `User.name` is populated
 * with an email-like value (contains `@`), the first token would render as
 * `Discover, who@x.test.`. Such tokens are treated as missing so the headline
 * falls back to the generic city grammar.
 */
export function deriveViewerFirstName(user: User | null): string | null {
  if (!user) return null;
  const first = (user.firstName ?? '').trim();
  if (first) return first;
  const name = (user.name ?? '').trim();
  if (name) {
    const firstToken = name.split(/\s+/)[0]?.trim();
    if (firstToken && !firstToken.includes('@')) return firstToken;
  }
  return null;
}

/**
 * Build the gated personalized-chrome model from the auth + discover-response
 * inputs. Returns `null` (→ generic chrome everywhere) unless the viewer is
 * authenticated, the authenticated envelope is present, and `meta.personalized`
 * is explicitly `true`.
 *
 * Taste counts come from `meta.tasteMatchedTotal` (API-1671 / WEB-786) — the
 * same corpus-wide field the results bar reads. Page-local `tasteMatchPct`
 * tallies are not consulted.
 */
export function buildDiscoverPersonalization(
  input: DiscoverPersonalizationInput,
): DiscoverPersonalization | null {
  const { user, isAuthenticated, envelope, personalized, total, tasteMatchedTotal } = input;

  // Strict gate: every condition must hold or we render generic (A) chrome.
  if (!isAuthenticated) return null;
  if (!envelope) return null;
  if (personalized !== true) return null;

  return {
    viewerName: deriveViewerFirstName(user),
    tasteMatchCount: typeof tasteMatchedTotal === 'number' ? tasteMatchedTotal : null,
    // WEB-786 — every numeric total survives, INCLUDING `0`. Collapsing a
    // measured zero to `null` here hid the coord strip's taste segment for a
    // resolved empty filter set while the hero and results bar still painted
    // `0` — the disagreeing-chrome defect this issue exists to fix.
    // `undefined` (not yet read) stays honest absence.
    totalCount: typeof total === 'number' ? total : null,
    // API-1667 — the uncapped totals, NOT `followedArtists.length`. The arrays
    // are a server-capped display page (12 / 8), so a viewer following 30
    // artists used to read "12 FROM ARTISTS YOU FOLLOW" — the page size stated
    // as a follow count.
    followedArtistCount: envelope.followedArtistsTotal,
    followedVenueCount: envelope.followedVenuesTotal,
  };
}
